import { Observable } from "@noya-app/observable";
import { ClientToServerMessage } from "./multiplayer";

type ActivityEventStream = {
  activityEvents: Observable<unknown[]>;
  isInitialized: Observable<boolean>;
};

export type StreamFilter = "all" | { resourceId: string };

export class ActivityEventsManager {
  activityEventStreams: Record<string, ActivityEventStream> = {};

  constructor(
    private sendMessage?: (message: ClientToServerMessage<any>) => void
  ) {}

  subscribe(filter: StreamFilter) {
    const streamId = filter === "all" ? "all" : `resource-${filter.resourceId}`;
    if (!this.activityEventStreams[streamId]) {
      this.activityEventStreams[streamId] = {
        activityEvents: new Observable<unknown[]>([]),
        isInitialized: new Observable<boolean>(false),
      };

      const cursor = undefined as string | undefined;

      this.sendMessage?.({
        type: "activityEvents.subscribe",
        streamId,
        ...(cursor && { cursor }),
      });
    }

    return streamId;
  }

  getStream(streamId: string): Observable<unknown[]> {
    return this.activityEventStreams[streamId]?.activityEvents;
  }

  unsubscribe(streamId: string) {
    this.sendMessage?.({
      type: "activityEvents.unsubscribe",
      streamId,
    });

    delete this.activityEventStreams[streamId];
  }

  setStreamEvents(streamId: string, activityEvents: unknown[]) {
    if (!this.activityEventStreams[streamId]) {
      this.activityEventStreams[streamId] = {
        activityEvents: new Observable<unknown[]>([]),
        isInitialized: new Observable<boolean>(false),
      };
    }

    const current = this.activityEventStreams[streamId].activityEvents.get();

    // Merge by id if possible, otherwise just concatenate new events first
    const merged = mergeActivityEvents(current, activityEvents);

    this.activityEventStreams[streamId].activityEvents.set(merged);
    this.activityEventStreams[streamId].isInitialized.set(true);
  }
}

function mergeActivityEvents(existing: unknown[], incoming: unknown[]) {
  const byId = new Map<string, unknown>();

  const getId = (e: any) => (e && typeof e === "object" ? e.id : undefined);

  for (const ev of [...(incoming || []), ...(existing || [])]) {
    const id = getId(ev);
    if (typeof id === "string") byId.set(id, ev);
  }

  return Array.from(byId.values());
}
